import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAccountSetup } from "../context/AccountSetupContext";
import { useUser } from "../context/UserContext";
import { useSubscription } from "../context/SubscriptionContext";
import PhoneVerificationPopup from "../usermgmt/PhoneVerificationPopup";
import "./css/AccountSetupJourney.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faCircle,
  faUserPlus,
  faEnvelope,
  faPhone,
  faCreditCard,
  faUsers,
  faShieldAlt,
  faSearch,
  faChartBar,
  faChevronDown,
  faChevronUp,
  faRocket,
} from "@fortawesome/free-solid-svg-icons";

/**
 * AccountSetupJourney Component (Account Setup Progress)
 *
 * Displays a horizontal progress bar showing the account setup stages
 * for account owners. Each stage has a button that users can click to
 * complete that part of the onboarding process. The first incomplete
 * non-optional stage is highlighted with a "Next Step" badge.
 *
 * Stages:
 * 1. Create Account (auto-completed if logged in)
 * 2. Verify Email
 * 3. Verify Phone Number
 * 4. Choose Service Plan
 * 5. Add Payment Method
 * 6. Add Users (optional if plan supports only 1 user)
 * 7. Set Up Simulation Profile
 * 8. Check for Scams
 */
function AccountSetupJourney({ setHeroContent }) {
  const navigate = useNavigate();
  const {
    setupProgress,
    isLoading,
    isAccountOwner,
    getProgressPercentage,
    refreshSetupProgress,
    markStageComplete,
  } = useAccountSetup();
  const { isDemoSeat, loggedInUser, updateLoggedInUser } = useUser();
  const { subscription, hasActiveSubscription } = useSubscription();
  const [showPhoneVerification, setShowPhoneVerification] = useState(false);
  const isTrialSubscription = Boolean(subscription?.isTrial);

  // Check if we should force-expand (navigated back from setup step, or
  // routed here post-login because the user has no active subscription)
  const shouldForceExpand =
    sessionStorage.getItem("expandSetupJourney") === "true" ||
    !hasActiveSubscription;
  if (sessionStorage.getItem("expandSetupJourney") === "true") {
    sessionStorage.removeItem("expandSetupJourney");
  }
  const [isExpanded, setIsExpanded] = useState(shouldForceExpand);
  const journeyStagesRef = useRef(null);
  const nextStepRef = useRef(null);

  // Scroll the "Next Step" stage into the center of the visible viewport
  useEffect(() => {
    if (!journeyStagesRef.current || !nextStepRef.current) return;
    const container = journeyStagesRef.current;
    const element = nextStepRef.current;
    // Only scroll when the stages row actually overflows
    if (container.scrollWidth <= container.clientWidth) return;
    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    // Element's distance from the left edge of the scrollable content
    const elementScrollLeft =
      container.scrollLeft + elementRect.left - containerRect.left;
    // Center the element relative to the full viewport width
    const scrollTo =
      elementScrollLeft - window.innerWidth / 2 + element.offsetWidth / 2;
    container.scrollLeft = Math.max(0, scrollTo);
  }, [setupProgress]);

  console.log("🎯 AccountSetupJourney: Rendering...");
  console.log("⏳ AccountSetupJourney: isLoading:", isLoading);
  console.log("👑 AccountSetupJourney: isAccountOwner:", isAccountOwner);
  console.log("📈 AccountSetupJourney: setupProgress:", setupProgress);

  // Don't render if not account owner or still loading
  if (isLoading || !isAccountOwner) {
    console.log(
      "🚫 AccountSetupJourney: Not rendering - isLoading:",
      isLoading,
      "isAccountOwner:",
      isAccountOwner,
    );
    return null;
  }

  console.log("✅ AccountSetupJourney: Conditions met, rendering component");

  // Define setup stages with their properties
  const stages = [
    {
      id: "accountCreated",
      label: "Create Account",
      icon: faUserPlus,
      completed: setupProgress.accountCreated,
      action: () => {}, // No action - auto-completed
      disabled: setupProgress.accountCreated,
      tooltip:
        "Your account has been successfully created. This step is automatically completed when you sign up.",
    },
    {
      id: "emailVerified",
      label: "Verify Email",
      icon: faEnvelope,
      completed: setupProgress.emailVerified,
      action: () => {},
      disabled: true,
      tooltip:
        "Verify your email address to ensure secure account recovery and important notifications. This is required to access the platform.",
    },
    {
      id: "phoneVerified",
      label: "Verify Phone",
      icon: faPhone,
      completed: setupProgress.phoneVerified,
      action: () => {
        if (!setupProgress.phoneVerified && loggedInUser?.phone) {
          setShowPhoneVerification(true);
        }
      },
      disabled: setupProgress.phoneVerified,
      tooltip: setupProgress.phoneVerified
        ? "Your phone number has been verified. This step is complete."
        : "Verify your phone number for multi-factor authentication and SMS security alerts. Click to resend a verification code.",
    },
    {
      id: "hasSubscription",
      label: "Subscribe to a Plan",
      icon: faCreditCard,
      completed: setupProgress.hasSubscription,
      partial: !setupProgress.hasSubscription && setupProgress.hasCheckoutDraft,
      action: () => setHeroContent("Subscriptions"),
      disabled: setupProgress.hasSubscription,
      tooltip: setupProgress.hasSubscription
        ? "Your plan is active and your subscription setup is complete."
        : setupProgress.hasCheckoutDraft
          ? "Plan selected. Next, add a payment method to activate your subscription."
          : "Choose the service plan that best fits your needs to begin subscription setup.",
    },
    {
      id: "hasPaymentMethod",
      label: "Add Payment Method",
      icon: faCreditCard,
      completed: setupProgress.hasPaymentMethod,
      action: () => setHeroContent("Payments:AddCard"),
      disabled:
        (!setupProgress.hasCheckoutDraft && !setupProgress.hasSubscription) ||
        setupProgress.hasPaymentMethod,
      tooltip: setupProgress.hasPaymentMethod
        ? setupProgress.hasCheckoutDraft && !setupProgress.hasSubscription
          ? "Payment method is ready. Click to return and activate your subscription."
          : "Your default payment method is on file."
        : !setupProgress.hasCheckoutDraft && !setupProgress.hasSubscription
          ? "Choose a plan first, then add your payment method to finish subscription setup."
          : "Add a default payment method so your selected plan can be activated.",
    },
    {
      id: "usersAdded",
      label: "Add Users",
      icon: faUsers,
      completed: setupProgress.usersAdded === "complete",
      partial: setupProgress.usersAdded === "partial",
      action: () => setHeroContent("Member User Info:Create"),
      disabled:
        setupProgress.usersAdded === "complete" ||
        setupProgress.usersAdded === "not-started",
      optional: setupProgress.usersAdded === "not-started",
      tooltip:
        setupProgress.usersAdded === "not-started"
          ? "Add family members or team members to your account. This is optional - your current plan supports only 1 user."
          : setupProgress.usersAdded === "partial"
            ? "Your plan supports multiple users. Click here to add family members or team members to start protecting them."
            : "You have added additional users to your account. Great job protecting your family or team!",
    },
    {
      id: "simulationProfileSetup",
      label: "Set up Simulation Profile",
      icon: faShieldAlt,
      completed: setupProgress.simulationProfileSetup,
      action: () => {
        sessionStorage.setItem("scrollToConfiguration", "true");
        sessionStorage.setItem("fromSetupJourney", "true");
        setHeroContent("Simulation Profile");
      },
      disabled: !setupProgress.hasSubscription, // Disabled if no active subscription
      tooltip: setupProgress.simulationProfileSetup
        ? "Your simulation profile is configured. Click to customize your security training difficulty level and preferences."
        : setupProgress.hasSubscription
          ? "Select a practice level to set up your simulation profile."
          : "Set up your security training simulation preferences. This requires an active subscription to access.",
    },
    {
      id: "scamCheckingComplete",
      label: "Check for Scams",
      icon: faSearch,
      completed: setupProgress.scamCheckingComplete,
      optional: true,
      action: () => {
        sessionStorage.setItem("fromSetupJourney", "true");
        setHeroContent("How do I...?");
      },
      disabled: !setupProgress.hasSubscription, // Disabled if no active subscription
      tooltip: setupProgress.hasSubscription
        ? "Try out our scam detection tools. Scan emails, texts, phone numbers, and social media posts for potential threats. (Optional)"
        : "Access powerful scam detection tools to analyze suspicious content. This requires an active subscription. (Optional)",
    },
    {
      id: "viewReports",
      label: "View Reports",
      icon: faChartBar,
      completed: false, // Never auto-completed, user can always view reports
      optional: true,
      action: () => {
        sessionStorage.setItem("fromSetupJourney", "true");
        setHeroContent("Shield Reports");
      },
      disabled: !setupProgress.hasSubscription, // Disabled if no active subscription
      tooltip: setupProgress.hasSubscription
        ? "View your security training reports, risk scores, and progress analytics. Track your improvement over time. (Optional)"
        : "Access detailed reports and analytics about your security training progress. This requires an active subscription. (Optional)",
    },
  ];
  // Hide Add Payment Method for demo seats and active trial subscriptions.
  const visibleStages =
    isDemoSeat || isTrialSubscription
      ? stages.filter((s) => s.id !== "hasPaymentMethod")
      : stages;

  const progressPercentage = getProgressPercentage();
  const isComplete = progressPercentage === 100;
  const shouldCollapse = progressPercentage > 90;

  // Determine the next step: first non-completed, non-optional stage
  const nextStepId = visibleStages.find((s) => !s.completed && !s.optional)?.id;

  return (
    <>
      {!hasActiveSubscription && (
        <div
          className="subscription-required-banner"
          role="alert"
          aria-live="polite"
        >
          <FontAwesomeIcon
            icon={faCreditCard}
            className="subscription-required-banner-icon"
          />
          <div className="subscription-required-banner-content">
            <h3 className="subscription-required-banner-title">
              An active subscription is required to access full functionality
            </h3>
            <p className="subscription-required-banner-message">
              Get started by choosing a plan that fits your needs. Once your
              subscription is active you'll unlock simulations, scam-checking
              tools, reports, and more.
            </p>
          </div>
          <button
            type="button"
            className="subscription-required-banner-cta"
            onClick={() => setHeroContent("Subscriptions")}
          >
            Subscribe to a Plan
          </button>
        </div>
      )}
      <div className={`account-setup-journey ${isComplete ? "completed" : ""}`}>
        <div
          className="journey-header"
          onClick={
            shouldCollapse ? () => setIsExpanded(!isExpanded) : undefined
          }
          style={shouldCollapse ? { cursor: "pointer" } : undefined}
          title={
            shouldCollapse
              ? isExpanded
                ? "Click to collapse journey"
                : "Click to expand journey"
              : undefined
          }
        >
          <div className="journey-title-section">
            <h2 className="journey-title">Account Setup Progress</h2>
            {shouldCollapse ? (
              <span className="journey-progress-badge">
                {progressPercentage}%
              </span>
            ) : (
              <div className="journey-progress-indicator">
                <div className="journey-progress-bar">
                  <div
                    className="journey-progress-fill"
                    style={{ width: `${progressPercentage}%` }}
                  ></div>
                </div>
                <span className="journey-progress-text">
                  {progressPercentage}%
                </span>
              </div>
            )}
            {shouldCollapse && (
              <button
                className="journey-toggle-button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsExpanded(!isExpanded);
                }}
                title={isExpanded ? "Collapse journey" : "Expand journey"}
              >
                <FontAwesomeIcon
                  icon={isExpanded ? faChevronUp : faChevronDown}
                  className="toggle-icon"
                />
              </button>
            )}
            {progressPercentage >= 90 && (
              <button
                className="journey-getting-started-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setHeroContent("Getting Started");
                }}
                title={
                  !loggedInUser?.emailVerified || !loggedInUser?.phoneVerified
                    ? "Complete email and phone verification to access Getting Started"
                    : "See what KinShield can do for you"
                }
                disabled={
                  !loggedInUser?.emailVerified || !loggedInUser?.phoneVerified
                }
              >
                <FontAwesomeIcon icon={faRocket} />
                <span>Getting Started</span>
              </button>
            )}
          </div>
        </div>

        {isTrialSubscription && (
          <div className="journey-trial-info-badge">
            Trial active: Payment method is not required during trial. You will
            be prompted to add one before trial ends.
          </div>
        )}

        {(!shouldCollapse || isExpanded) && (
          <div className="journey-stages" ref={journeyStagesRef}>
            {visibleStages.map((stage, index) => (
              <div
                key={stage.id}
                className="journey-stage-wrapper"
                ref={stage.id === nextStepId ? nextStepRef : null}
              >
                {stage.id === nextStepId && (
                  <span className="next-step-badge">Next Step</span>
                )}
                <div className="stage-button-column">
                  <button
                    className={`journey-stage-button ${
                      stage.completed ? "completed" : ""
                    } ${stage.partial ? "partial" : ""} ${
                      stage.disabled && !stage.completed && !stage.partial
                        ? "disabled"
                        : ""
                    } ${stage.optional ? "optional" : ""} ${
                      stage.id === nextStepId ? "next-step" : ""
                    }`}
                    onClick={stage.action}
                    disabled={
                      stage.disabled && !stage.completed && !stage.partial
                    }
                  >
                    {stage.optional && (
                      <span className="optional-badge">Optional</span>
                    )}
                    {stage.partial && (
                      <span className="partial-badge">In Progress</span>
                    )}
                    <div className="stage-icon-wrapper">
                      <FontAwesomeIcon
                        icon={stage.icon}
                        className="stage-icon"
                      />
                    </div>
                    <span className="stage-label">{stage.label}</span>
                    {stage.completed && (
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        className="stage-check-icon"
                      />
                    )}
                    {stage.tooltip && (
                      <div className="stage-tooltip">{stage.tooltip}</div>
                    )}
                  </button>
                  <span className="stage-step-number">Step {index + 1}</span>
                </div>
                {index < visibleStages.length - 1 && (
                  <div
                    className={`journey-connector ${
                      stage.completed ? "completed" : ""
                    }`}
                  ></div>
                )}
              </div>
            ))}
          </div>
        )}

        {!shouldCollapse && (
          <p className="journey-setup-status">
            Your account setup is not complete yet. Continue with the Account
            Setup Journey above to fully activate your account and unlock all
            features.
          </p>
        )}
      </div>

      {showPhoneVerification && loggedInUser && (
        <PhoneVerificationPopup
          user={loggedInUser}
          onClose={() => setShowPhoneVerification(false)}
          onVerificationComplete={async () => {
            setShowPhoneVerification(false);
            // Immediately mark phone step complete so the UI updates without
            // waiting for the full async loadSetupProgress round-trip.
            markStageComplete("phoneVerified");
            // Also update loggedInUser so future refreshes read the correct value
            if (loggedInUser) {
              updateLoggedInUser({ ...loggedInUser, phoneVerified: true });
            }
            // Background refresh to sync all other progress data
            if (refreshSetupProgress) {
              await refreshSetupProgress();
            }
          }}
        />
      )}
    </>
  );
}

export default AccountSetupJourney;
